import { Fragment, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { CodedStatusBadge } from '../../components/ui/CodedStatusBadge';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { usePermissions } from '../../contexts/PermissionsContext';
import {
  SupportInboundAttachment,
  SupportInboundMessage,
  getInboundAttachmentDownloadUrl,
  listInboundMessageAttachments,
  listInboundMessages,
  reprocessInboundMessage,
} from '../../services/supportInboundApi';
import { getServiceTicket } from '../../services/customerSuccessTicketsApi';

const STATUS_LABELS: Record<string, string> = {
  Pending: 'Pendente',
  Processing: 'Processando',
  Processed: 'Processado',
  Ignored: 'Ignorado',
  Failed: 'Falhou',
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function InboundMessagesPage() {
  const { canWriteModule } = usePermissions();
  const canReprocess = canWriteModule('customer-success.inbound');
  const [searchParams, setSearchParams] = useSearchParams();
  const ticketIdFilter = searchParams.get('ticketId') ?? undefined;
  const statusFilter = searchParams.get('status') ?? '';

  const [items, setItems] = useState<SupportInboundMessage[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [ticketFilterLabel, setTicketFilterLabel] = useState<string | null>(null);
  const [expandedMessageId, setExpandedMessageId] = useState<string | null>(null);
  const [attachmentsByMessage, setAttachmentsByMessage] = useState<
    Record<string, SupportInboundAttachment[]>
  >({});
  const [loadingAttachmentsId, setLoadingAttachmentsId] = useState<string | null>(null);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  async function loadData(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const result = await listInboundMessages({
        page: targetPage,
        pageSize,
        ticketId: ticketIdFilter,
        status: statusFilter || undefined,
      });
      setItems(result.items);
      setTotalCount(result.totalCount);
      setPage(result.page);
    } catch {
      toast.error('Falha ao carregar mensagens inbound.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setPage(1);
    setExpandedMessageId(null);
    setAttachmentsByMessage({});
  }, [ticketIdFilter, statusFilter]);

  useEffect(() => {
    if (!ticketIdFilter) {
      setTicketFilterLabel(null);
      return;
    }

    void (async () => {
      try {
        const ticket = await getServiceTicket(ticketIdFilter);
        setTicketFilterLabel(ticket.ticketCode);
      } catch {
        setTicketFilterLabel(ticketIdFilter);
      }
    })();
  }, [ticketIdFilter]);

  useEffect(() => {
    void loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, ticketIdFilter, statusFilter]);

  async function handleReprocess(message: SupportInboundMessage): Promise<void> {
    try {
      await reprocessInboundMessage(message.id);
      toast.success('Mensagem reprocessada.');
      await loadData(page);
    } catch {
      toast.error('Falha ao reprocessar mensagem.');
    }
  }

  async function toggleAttachments(message: SupportInboundMessage): Promise<void> {
    if (expandedMessageId === message.id) {
      setExpandedMessageId(null);
      return;
    }

    setExpandedMessageId(message.id);

    if (attachmentsByMessage[message.id]) {
      return;
    }

    setLoadingAttachmentsId(message.id);
    try {
      const attachments = await listInboundMessageAttachments(message.id);
      setAttachmentsByMessage((current) => ({ ...current, [message.id]: attachments }));
    } catch {
      toast.error('Falha ao carregar anexos.');
      setExpandedMessageId(null);
    } finally {
      setLoadingAttachmentsId(null);
    }
  }

  async function handleDownloadAttachment(
    messageId: string,
    attachment: SupportInboundAttachment,
  ): Promise<void> {
    try {
      const result = await getInboundAttachmentDownloadUrl(messageId, attachment.id);
      window.open(result.downloadUrl, '_blank', 'noopener,noreferrer');
    } catch {
      toast.error(`Falha ao baixar ${attachment.fileName}.`);
    }
  }

  function clearTicketFilter(): void {
    const next = new URLSearchParams(searchParams);
    next.delete('ticketId');
    setSearchParams(next);
  }

  function setStatusFilter(status: string): void {
    const next = new URLSearchParams(searchParams);
    if (status) {
      next.set('status', status);
    } else {
      next.delete('status');
    }
    setSearchParams(next);
  }

  return (
    <PageShell>
      <PageHeader
        title="Mensagens inbound"
        description="Auditoria de entradas por API, e-mail e chat antes e depois da triagem."
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="erp-label min-w-[180px]">
          Status
          <select
            className="erp-input"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">Todos</option>
            <option value="Pending">Pendente</option>
            <option value="Processing">Processando</option>
            <option value="Processed">Processado</option>
            <option value="Ignored">Ignorado</option>
            <option value="Failed">Falhou</option>
          </select>
        </label>
      </div>

      {ticketIdFilter ? (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 text-sm">
          <span>
            Filtrando pelo chamado{' '}
            <Link
              className="font-medium text-primary-700 hover:underline"
              to={`/customer-success/tickets?selected=${ticketIdFilter}`}
            >
              {ticketFilterLabel ?? ticketIdFilter}
            </Link>
          </span>
          <button type="button" className="erp-btn-secondary text-xs" onClick={clearTicketFilter}>
            Limpar filtro
          </button>
        </div>
      ) : null}

      {loading ? (
        <LoadingSpinner label="Carregando mensagens..." />
      ) : (
        <>
          <div className="erp-card overflow-x-auto">
            <table className="erp-table w-full">
              <thead>
                <tr>
                  <th>Recebida</th>
                  <th>Canal</th>
                  <th>Remetente</th>
                  <th>Assunto</th>
                  <th>Status</th>
                  <th>Anexos</th>
                  <th>Ticket</th>
                  {canReprocess ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <Fragment key={item.id}>
                    <tr>
                      <td className="whitespace-nowrap text-sm">
                        {new Date(item.receivedAtUtc).toLocaleString('pt-BR')}
                      </td>
                      <td>{item.channel}</td>
                      <td>{item.fromAddress}</td>
                      <td className="max-w-xs truncate">{item.subject ?? item.bodyText}</td>
                      <td>
                        <CodedStatusBadge
                          status={item.processingStatus}
                          label={STATUS_LABELS[item.processingStatus] ?? item.processingStatus}
                        />
                      </td>
                      <td>
                        {item.attachmentCount > 0 ? (
                          <button
                            type="button"
                            className="text-primary-600 hover:underline"
                            onClick={() => void toggleAttachments(item)}
                          >
                            {item.attachmentCount} arquivo(s)
                          </button>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        {item.resultingTicketId ? (
                          <Link
                            className="text-primary-600 hover:underline"
                            to={`/customer-success/tickets?selected=${item.resultingTicketId}`}
                          >
                            Ver chamado
                          </Link>
                        ) : (
                          '—'
                        )}
                      </td>
                      {canReprocess ? (
                        <td>
                          <button
                            type="button"
                            className="erp-btn-integration erp-btn-sm"
                            onClick={() => void handleReprocess(item)}
                          >
                            Reprocessar
                          </button>
                        </td>
                      ) : null}
                    </tr>
                    {expandedMessageId === item.id ? (
                      <tr key={`${item.id}-attachments`}>
                        <td colSpan={canReprocess ? 8 : 7} className="bg-secondary-50 px-4 py-3">
                          {loadingAttachmentsId === item.id ? (
                            <p className="text-sm text-secondary-500">Carregando anexos...</p>
                          ) : (attachmentsByMessage[item.id] ?? []).length === 0 ? (
                            <p className="text-sm text-secondary-500">Nenhum anexo encontrado.</p>
                          ) : (
                            <ul className="space-y-2">
                              {(attachmentsByMessage[item.id] ?? []).map((attachment) => (
                                <li
                                  key={attachment.id}
                                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-secondary-200 bg-white px-3 py-2"
                                >
                                  <div>
                                    <p className="text-sm font-medium text-secondary-900">
                                      {attachment.fileName}
                                    </p>
                                    <p className="text-xs text-secondary-500">
                                      {formatFileSize(attachment.fileSizeBytes)} ·{' '}
                                      {new Date(attachment.createdAtUtc).toLocaleString('pt-BR')}
                                      {attachment.promotedToTicketAttachmentId
                                        ? ' · vinculado ao chamado'
                                        : ''}
                                    </p>
                                  </div>
                                  <button
                                    type="button"
                                    className="erp-btn-secondary text-sm"
                                    onClick={() =>
                                      void handleDownloadAttachment(item.id, attachment)
                                    }
                                  >
                                    Baixar
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <ListPaginationBar
            totalCount={totalCount}
            page={page}
            pageSize={pageSize}
            onPrevious={() => setPage((current) => Math.max(1, current - 1))}
            onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
          />
        </>
      )}
    </PageShell>
  );
}
